<div class="flex flex-col w-full h-full">
  <h1 class="mb-8 text-light-300 text-3xl font-medium">
    Property management <span class="text-xl text-red-100">(Demo Mode)</span>
  </h1>

  <div class="flex flex-col gap-12 w-full h-full">
    @if(_propertyManagementService.isPropertyManagementLoading$ | async){
    <ng-container *ngTemplateOutlet="loadingSkeleton" />
    } @else {
    <!-- Property details -->
    <p-card>
      <div class="flex flex-row gap-4 items-center">
        <h2 class="font-bold text-base text-light-300">
          <a
            class="hover:underline"
            [href]="
              '/home/properties/view/' + propertyManagement?.property?.$id
            "
            >{{ propertyManagement?.property?.name }}</a
          >
        </h2>
        <i-tabler
          name="pin-invoke"
          class="custom-svg cursor-pointer"
          (click)="onNavigateToProperty(propertyManagement?.property?.$id)"
        ></i-tabler>
      </div>
    </p-card>

    <!-- Portfolio value -->
    <p-card class="w-full">
      <div class="flex flex-col gap-1">
        <div
          class="flex flex-row gap-2 items-center font-medium text-base text-light-300"
        >
          <h2>Property value</h2>
          <svg-icon
            src="assets/icons/info.svg"
            svgClass="h-4 w-4"
            (click)="isTouchDevice && info.toggle($event)"
            (mouseenter)="!isTouchDevice && info.show($event)"
            (mouseleave)="!isTouchDevice && info.hide()"
          />

          <p-popover #info>
            <p class="w-56 text-light-0 text-base">
              The total value of your property that you manage (based on the
              latest valuation of the property)
            </p>
          </p-popover>
        </div>

        <p class="font-bold text-3xl text-light-300">
          {{ propertyManagement?.totalValue | customCurrency : currency }}
        </p>
      </div>

      <app-portfolio-growth-chart
        [dates]="growthOverviewChartDates"
        [values]="propertyManagement?.growthOverviewChartValues || []"
      />
    </p-card>

    <!-- Key financials -->
    <div class="flex flex-col gap-4">
      <h3 class="font-bold text-xl text-light-300">Key financials</h3>

      <div class="flex flex-col sm:flex-row gap-6">
        <!-- Monthly income -->
        <p-card class="w-full sm:basis-1/3">
          <div class="flex flex-col gap-2">
            <div
              class="flex flex-col xl:flex-row gap-2 items-start xl:items-center justify-between"
            >
              <svg-icon
                src="assets/icons/coins.svg"
                svgClass="h-6 w-6 text-blue-100"
              />

              <p class="font-semibold text-xl text-light-200">
                {{
                  propertyManagement?.monthlyIncome | customCurrency : currency
                }}
              </p>
            </div>

            <div
              class="flex flex-col xl:flex-row gap-2 items-start xl:items-center justify-between"
            >
              <p
                class="flex flex-row gap-2 items-center text-sm text-light-100"
              >
                Monthly income
                <svg-icon
                  src="assets/icons/info.svg"
                  svgClass="h-4 w-4"
                  (click)="isTouchDevice && monthlyIncome.toggle($event)"
                  (mouseenter)="!isTouchDevice && monthlyIncome.show($event)"
                  (mouseleave)="!isTouchDevice && monthlyIncome.hide()"
                />

                <p-popover #monthlyIncome>
                  <p class="w-56 text-light-0 text-base">
                    This displays the most recent rental income deposited.
                  </p>
                </p-popover>
              </p>

              <p class="text-sm text-light-100">
                {{ propertyManagement?.financialPeriod | date : "MMMM YYYY" }}
              </p>
            </div>
          </div>
        </p-card>

        <!-- Total rental income -->
        <p-card class="w-full sm:basis-1/3">
          <div class="flex flex-col gap-2">
            <div
              class="flex flex-col xl:flex-row gap-2 items-start xl:items-center justify-between"
            >
              <svg-icon
                src="assets/icons/money-up.svg"
                svgClass="h-6 w-6 text-blue-100"
              />

              <p class="font-semibold text-xl text-light-200">
                {{
                  propertyManagement?.totalRentalIncome
                    | customCurrency : currency
                }}
              </p>
            </div>

            <div
              class="flex flex-col xl:flex-row gap-2 items-start xl:items-center justify-between"
            >
              <p
                class="flex flex-row gap-2 items-center text-sm text-light-100"
              >
                Total rental income
                <svg-icon
                  src="assets/icons/info.svg"
                  svgClass="h-4 w-4"
                  (click)="isTouchDevice && rentalIncome.toggle($event)"
                  (mouseenter)="!isTouchDevice && rentalIncome.show($event)"
                  (mouseleave)="!isTouchDevice && rentalIncome.hide()"
                />

                <p-popover #rentalIncome>
                  <p class="w-56 text-light-0 text-base">
                    The total amount of monthly rent paid.
                  </p>
                </p-popover>
              </p>

              <p class="text-sm text-light-100">
                {{ propertyManagement?.financialPeriod | date : "MMMM YYYY" }}
              </p>
            </div>
          </div>
        </p-card>

        <!-- Total appreciation -->
        <p-card class="w-full sm:basis-1/3">
          <div class="flex flex-col gap-2">
            <div
              class="flex flex-col xl:flex-row gap-2 items-start xl:items-center justify-between"
            >
              <svg-icon
                src="assets/icons/graph.svg"
                svgClass="h-6 w-6 text-blue-100"
              />

              <p class="font-semibold text-xl text-light-200">
                {{
                  propertyManagement?.totalAppreciation
                    | customCurrency : currency
                }}
              </p>
            </div>

            <div
              class="flex flex-col xl:flex-row gap-2 items-start xl:items-center justify-between"
            >
              <p
                class="flex flex-row gap-2 items-center text-sm text-light-100"
              >
                Total appreciation
                <svg-icon
                  src="assets/icons/info.svg"
                  svgClass="h-4 w-4"
                  (click)="isTouchDevice && totalAppreciation.toggle($event)"
                  (mouseenter)="
                    !isTouchDevice && totalAppreciation.show($event)
                  "
                  (mouseleave)="!isTouchDevice && totalAppreciation.hide()"
                />

                <p-popover #totalAppreciation>
                  <p class="w-56 text-light-0 text-base">
                    Unrealised gains or losses from the latest valuations of
                    this property
                  </p>
                </p-popover>
              </p>

              <p class="text-sm text-light-100">
                {{ propertyManagement?.financialPeriod | date : "MMMM YYYY" }}
              </p>
            </div>
          </div>
        </p-card>
      </div>
    </div>

    <!-- Property transactions -->
    <div class="flex flex-col gap-4">
      <div class="flex flex-row gap-5 items-center justify-between">
        <h3 class="font-bold text-xl text-light-300">
          Property transactions (income/ outcome)
        </h3>

        <p-button
          severity="primary"
          type="submit"
          label="Add transaction"
          size="small"
          [fluid]="true"
          (onClick)="onShowAddTrasactionDialog()"
        />
      </div>

      <p-card class="w-full">
        <p-table
          [value]="propertyManagement?.transactions || []"
          [scrollable]="true"
          scrollHeight="370px"
          stripedRows
          [tableStyle]="{ 'min-width': '50rem' }"
          size="large"
        >
          <ng-template #header>
            <tr>
              <th>Type</th>
              <th>Date</th>
              <th>Amount</th>
            </tr>
          </ng-template>
          <ng-template #body let-transaction>
            <tr>
              <td>
                <div class="flex flex-row gap-2">
                  <!--  -->
                  @switch (transaction.type) {
                  <!--  -->
                  @case(WalletTransaction.RENTAL_PAID) {Rental Payment}
                  @case(WalletTransaction.PROPERTY_TAX) {Property Tax}
                  @case(WalletTransaction.UTILITIES_PAID) {Utilities Paid}
                  @case(WalletTransaction.FURNITURE_PURCHASE) {Furniture
                  Purchase}
                  <!--  -->
                  @case(WalletTransaction.SECURITY_SERVICE) {Security Service}
                  <!--  -->

                  @case(WalletTransaction.LEGAL_FEES) {Legal Fees}
                  @case(WalletTransaction.MANAGEMENT_FEE) {Management Fee}
                  @case(WalletTransaction.INSURANCE_PAYMENT) {Insurance Payment}
                  <!--  -->
                  @case(WalletTransaction.REPAIRS_AND_MAINTENANCE) {Repairs &
                  Maintenance}
                  <!--  -->
                  @case(WalletTransaction.OTHER_EXPENSE) {Other Expense}
                  @case(WalletTransaction.PROFIT_DISTRIBUTION) {Profit
                  Distribution to Shareholders}
                  <!--  -->
                  }
                  <!--  -->
                </div>
              </td>
              <td>
                <p class="text-base text-light-200">
                  {{ transaction.date | date : "MMMM YYYY" }}
                </p>
              </td>
              <td>
                <p
                  class="text-base text-light-200"
                  [ngClass]="{
                    '!text-green-100':
                      transaction.moneyFlow === MoneyFlowType.INCOME,
                    '!text-red-100':
                      transaction.moneyFlow === MoneyFlowType.OUTCOME
                  }"
                >
                  @if(transaction.moneyFlow === MoneyFlowType.INCOME){+}
                  <!--  -->
                  @else{-}
                  {{ transaction.amount | customCurrency : currency }}
                </p>
              </td>
            </tr>
          </ng-template>
          <ng-template #emptymessage>
            <tr>
              <td colspan="6">
                <div
                  class="flex flex-col gap-4 items-center justify-center text-base text-light-300 p-5"
                >
                  <svg-icon src="assets/icons/clock.svg" svgClass="h-8 w-8" />
                  <p>No transactions found</p>
                </div>
              </td>
            </tr>
          </ng-template>
        </p-table>
      </p-card>
    </div>

    <!--  -->
    <!--  -->

    <p-card class="w-full">
      <div class="flex flex-col gap-6">
        <div class="flex flex-row items-center justify-between gap-2">
          <h3 class="font-bold text-xl text-light-300">
            Documents
            <span>({{ propertyManagement?.documents?.length || 0 }})</span>
          </h3>

          <p-button
            severity="primary"
            type="submit"
            label="Add document"
            size="small"
            [fluid]="true"
            (onClick)="onShowAddDocumentDialog()"
          />
        </div>

        <ul class="flex flex-col gap-6">
          @for (document of propertyManagement?.documents; track $index) {
          <a
            class="flex flex-row gap-4 items-center justify-between p-4 border-[1.5px] text-light-300 border-light-150 rounded-lg cursor-pointer hover:bg-light-150 hover:text-blue-150 hover:underline"
            [href]="document.url"
            target="_blank"
          >
            <div class="flex flex-row gap-2 items-center pointer-events-none">
              <svg-icon
                [src]="'assets/icons/' + document.type + '.svg'"
                svgClass="h-6 w-6"
              />
              <p class="text-sm">
                {{ document.name }}
              </p>
            </div>

            <div class="flex flex-row gap-6">
              @if(document.canBeDeleted){
              <svg-icon
                src="assets/icons/trash-bin.svg"
                svgClass="h-5 w-5 cursor-pointer text-red-100"
                (click)="onRemoveProperty($index, $event)"
              />
              }

              <svg-icon
                [src]="'assets/icons/download.svg'"
                svgClass="h-5 w-5 text-blue-100"
                (click)="onDownloadDocument(document, $event)"
              />
            </div>
          </a>
          }
        </ul>
      </div>
    </p-card>
    }
  </div>
</div>

<ng-template #loadingSkeleton>
  <div class="flex flex-col gap-12 w-full h-full">
    <p-skeleton width="100%" height="200px" />
    <p-skeleton width="100%" height="300px" />

    <div class="flex flex-col sm:flex-row gap-6">
      <p-card class="w-full basis-1/3">
        <div class="flex flex-col gap-2">
          <p-skeleton height="24px" width="28px" />
          <p-skeleton height="24px" width="120px" />
          <p-skeleton height="24px" width="50px" />
        </div>
      </p-card>

      <p-card class="w-full basis-1/3">
        <div class="flex flex-col gap-2">
          <p-skeleton height="24px" width="28px" />
          <p-skeleton height="24px" width="120px" />
          <p-skeleton height="24px" width="50px" />
        </div>
      </p-card>

      <p-card class="w-full basis-1/3">
        <div class="flex flex-col gap-2">
          <p-skeleton height="24px" width="28px" />
          <p-skeleton height="24px" width="120px" />
          <p-skeleton height="24px" width="50px" />
        </div>
      </p-card>
    </div>

    <p-skeleton width="100%" height="200px" />
    <p-skeleton width="100%" height="300px" />
  </div>
</ng-template>

<p-dialog
  header="Add document"
  [modal]="true"
  [(visible)]="showAddDocumentDialog"
  [style]="{ width: '512px' }"
>
  <div class="flex flex-col gap-5">
    <form
      [formGroup]="addDocumentForm"
      class="flex flex-col items-start gap-5"
      (ngSubmit)="onAddDocument()"
    >
      <div class="flex flex-col gap-2 w-full">
        <label class="font-bold text-sm text-light-300" for="fileLabel">
          Description
        </label>

        <input
          formControlName="fileLabel"
          pInputText
          type="text"
          pSize="large"
          placeholder="Description"
          class="w-full p-inputtext"
          [style]="{
            'ng-invalid ng-dirty':
              fDocuments['fileLabel'].invalid && fDocuments['fileLabel'].touched
          }"
        />
      </div>

      <!-- File upload -->
      <div class="flex flex-col items-start gap-2 w-full">
        <p-fileUpload
          #fileUploader
          mode="basic"
          chooseLabel="Choose"
          chooseIcon="pi pi-upload"
          name="fileUpload[]"
          [customUpload]="true"
          [auto]="false"
          [accept]="'.pdf,.xls,.xlsx'"
          [maxFileSize]="10000000"
          (uploadHandler)="onFileUploaded($event)"
          (onSelect)="onFileSelect($event)"
        >
        </p-fileUpload>
      </div>

      <p-fluid>
        <p-button
          type="submit"
          label="Add document"
          size="large"
          styleClass="w-full"
          [disabled]="!addDocumentForm['valid']"
          [fluid]="true"
        />
      </p-fluid>
    </form>
  </div>
</p-dialog>

<p-dialog
  header="Add Transaction Details"
  [modal]="true"
  [(visible)]="showAddTransactionfialog"
  styleClass="overflow-hidden"
  [style]="{ width: '512px' }"
>
  <div class="flex flex-col gap-5">
    <form
      [formGroup]="addTransactionForm"
      class="flex flex-col items-start gap-5"
      (ngSubmit)="onAddTransaction()"
    >
      <div class="flex flex-col gap-2 w-full">
        <label class="font-bold text-sm text-light-300" for="date">
          Date
        </label>

        <p-datepicker
          id="date"
          name="date"
          formControlName="date"
          [iconDisplay]="'input'"
          [showIcon]="true"
          inputId="icondisplay"
          class="w-full"
          styleClass="w-full"
        />
      </div>

      <div class="flex flex-col gap-2 w-full">
        <label class="font-bold text-sm text-light-300" for="type">
          Type
        </label>

        <p-select
          formControlName="type"
          [options]="walletTransactionList"
          optionLabel="name"
          placeholder="Select transaction type"
          class="w-full"
          size="large"
        />
      </div>

      <div class="flex flex-col gap-2 w-full">
        <label class="font-bold text-sm text-light-300" for="moneyFlow">
          Select money flow
        </label>

        <p-select
          formControlName="moneyFlow"
          [options]="moneyFlowTypeList"
          optionLabel="name"
          placeholder="Select money flow"
          class="w-full"
          size="large"
        />
      </div>

      <div class="flex flex-col gap-2 w-full">
        <label class="font-bold text-sm text-light-300" for="amount">
          Amount
        </label>

        <p-inputnumber formControlName="amount" placeholder="Enter amount" />
      </div>

      <p-fluid>
        <p-button
          type="submit"
          label="Add transaction"
          size="large"
          styleClass="w-full"
          [disabled]="!addTransactionForm['valid']"
          [fluid]="true"
        />
      </p-fluid>
    </form>
  </div>
</p-dialog>
